import * as React from 'react';
import styled from 'styled-components';
import { Node } from 'slate';
import cl from 'classnames';
import { noop } from 'lodash';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { RendererWithPlugins } from '@doltech/ui/lib/slatePlugins/components/core/readonly/RendererWithPlugins';
import { onContentItemClick } from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/utils';
import { ContentItemProps } from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/TableOfContentType';
import { Dot } from '@doltech/ui/lib/oflfine-course-management/layout/Dot';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { IconReadOnlyPlugin } from '@doltech/ui/lib/figma/DolEditor/elements/icons/IconPlugin';

/* stylelint-disable no-descending-specificity */

const ContentItemGroupSubLayout = styled.ol`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  padding-left: 20px;
  margin-left: 2px;
  margin-bottom: 12px;
  box-shadow: 1px 0 0 0 #d8d9dc inset;
  color: ${colorsV2.gray100};
  margin-top: 12px;
  gap: 8px;

  > div {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: max-content minmax(0, 1fr);
    align-items: baseline;
    gap: 8px;
  }
`;

const SingleItem = styled.div`
  a {
    color: ${colorsV2.gray120} !important;
  }

  &.activeSub {
    a {
      color: ${colorsV2.purple100} !important;
    }
  }
`;
const ItemWithSubItemWrapper = styled.li`
  list-style-type: none;

  ${ContentItemGroupSubLayout} {
    display: none;
    margin-top: 8px;
    padding-left: 20px;
  }

  &.show-sub {
    ${ContentItemGroupSubLayout} {
      display: grid;
    }
  }
`;

const Wrapper = styled.ol`
  list-style-type: none;
  padding-inline-start: 0;
  margin-bottom: 0;

  a,
  span {
    color: ${colorsV2.gray120} !important;

    :hover {
      color: ${colorsV2.gray100} !important;
    }
  }

  .active {
    font-weight: 500;
    color: ${colorsV2.purple100} !important;

    :hover {
      color: ${colorsV2.purple80} !important;
    }

    a {
      color: ${colorsV2.purple100} !important;

      :hover {
        color: ${colorsV2.purple80} !important;
      }
    }
  }

  .high-light {
    color: ${colorsV2.gray140} !important;
    margin-right: 8px;
  }

  .sub-item-content {
    display: flex;
    align-items: center;
    list-style-type: none;
  }

  .sub-item-content-nested {
    display: flex;
    list-style-type: none;

    .dot-nested-item {
      margin-top: 9px;
    }
  }
`;

const plugins = [IconReadOnlyPlugin()];

export const ContentItemGroupWithEditorGrammarLanding = (props: ContentItemProps) => {
  const { currentSectionId, scrollContainerElm, number, onClick = noop, ...item } = props;
  const active = React.useMemo(() => item.id === currentSectionId, [currentSectionId, item.id]);

  const editorContent = React.useMemo(() => {
    const { item: data } = item as any;
    return [data];
  }, [item]);

  const itemText = React.useMemo(() => {
    const { item: data } = item as any;
    return [data].map((n) => Node.string(n)).join('   ');
  }, [item]);

  const handleOnClick = () => {
    onContentItemClick(item.id, scrollContainerElm);
    onClick?.();
  };
  const isParentActive = active || item?.sub?.some((it) => it.id === currentSectionId);
  if (item?.sub?.length) {
    return (
      <Wrapper>
        <ItemWithSubItemWrapper
          className={cl({
            'show-sub': isParentActive,
          })}
        >
          <ResponsiveTypography.Text
            href={`#${item.id}`}
            ellipsis={{ tooltip: true }}
            className={cl({ active: isParentActive })}
            onClick={handleOnClick}
          >
            <RendererWithPlugins plugins={plugins} value={editorContent} />
          </ResponsiveTypography.Text>
          <ContentItemGroupSubLayout className="content-item-group-sub">
            {item.sub.map((child, idx) => {
              return (
                <li key={idx} className="sub-item-content">
                  <Dot size={4} color={colorsV2.black20} />
                  <ContentItemGroupWithEditorGrammarLanding
                    currentSectionId={currentSectionId}
                    scrollContainerElm={scrollContainerElm}
                    {...child}
                  />
                </li>
              );
            })}
          </ContentItemGroupSubLayout>
        </ItemWithSubItemWrapper>
      </Wrapper>
    );
  }
  return (
    <SingleItem className={cl({ activeSub: active })}>
      <ResponsiveTypography.Text ellipsis={{ tooltip: itemText }} onClick={handleOnClick}>
        <a href={`#${item.id}`}>
          <RendererWithPlugins plugins={plugins} value={editorContent} />
        </a>
      </ResponsiveTypography.Text>
    </SingleItem>
  );
};
